// A stock count (UI design §3.6; schedule and owners CR_20260914). Each place's Stock counts tab lists its counts
// (components/place/PlaceCounts); a count is opened for a site (window, default the last 24 h). A site with count
// owners is counted by its owners and managers only (decision C5).
//
// The detail page was four bucket cards side by side, each a list of codes, with the verification method buried in
// a line of secondary text and an action button reading "Seen" (owner, 2026-09-18: "'seen' is not a good process").
// It is now one table — the day's snapshot, a row per asset — whose "Verified by" column says whether a QR label was
// scanned, a gateway or phone heard the tag over BLE, or somebody simply confirmed it by eye. Bucket chips filter
// the same table instead of splitting it into four.
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { type CountStatus, staleCount } from '@fleetspoke/core/counts';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { Loading, LoadError, Page } from '../components/Page';
import { useProfileNames } from '../data/billing';
import { useLiveInvalidate } from '../data/realtime';
import {
  type Bucket,
  type CountRow,
  useClearLine,
  useCloseCount,
  useCount,
  useCountStatus,
  useOpenCount,
} from '../data/workflow';
import { stateChip, verifiedBy } from '../lib/counts';
import { isoDateIn } from '../lib/billing';
import { calendarDate, dateTime } from '../lib/time';
import { useSession } from '../auth/session';

export const BUCKETS: Bucket[] = ['found', 'not_found', 'unexpected', 'unverified'];
const BUCKET_COLOR = {
  found: 'success',
  not_found: 'error',
  unexpected: 'warning',
  unverified: 'default',
} as const;

/** The RPC's refusal in words: one open count per site (55000), owners only (42501), else the message. */
function countError(error: unknown, t: (k: string) => string): string {
  const code = (error as { code?: string }).code;
  if (code === '55000') return t('counts.already_open');
  if (code === '42501') return t('counts.not_allowed');
  return (error as Error).message;
}

/** Opens a count; the site list holds only sites the person may count (C5). Also used by the site board. */
export function NewCountDialog({ onClose, siteId: initial }: { onClose(): void; siteId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = useCountStatus();
  const open = useOpenCount();
  const [siteId, setSiteId] = useState(initial ?? '');
  const [hours, setHours] = useState(24);
  const sites = (status.data ?? []).filter((s) => s.can_count || s.site_id === initial);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('counts.new')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            select
            label={t('counts.site')}
            value={sites.some((s) => s.site_id === siteId) ? siteId : ''}
            onChange={(e) => setSiteId(e.target.value)}
          >
            {sites.map((s) => (
              <MenuItem key={s.site_id} value={s.site_id}>
                {s.site_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('counts.window')}
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            helperText={t('counts.window_help')}
          >
            {[6, 24, 48, 168].map((h) => (
              <MenuItem key={h} value={h}>
                {t('counts.last_hours', { count: h })}
              </MenuItem>
            ))}
          </TextField>
          {open.error ? <Alert severity="error">{countError(open.error, t)}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!siteId || open.isPending}
          onClick={() =>
            open.mutate(
              { siteId, hours },
              { onSuccess: (r) => void navigate(`/counts/${r.count_id}`) },
            )
          }
        >
          {t('counts.start')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * "Next Tue 15 Sep" chip + result chip helpers shared with the site board and the Sites cards.
 * `standalone` names the subject ("Stock count: …") for cards where the chip sits among asset figures.
 */
export function CountStateChip({ s, standalone }: { s: CountStatus; standalone?: boolean }) {
  const { t, i18n } = useTranslation();
  const chip = stateChip(s, t, (d) => calendarDate(d, i18n.language), { standalone });
  return (
    <Chip
      size="small"
      color={chip.color}
      variant={chip.color === 'default' ? 'outlined' : 'filled'}
      label={chip.label}
      data-testid={`count-state-${s.site_id}`}
    />
  );
}

/** One labelled fact in the summary card: a small caps label over the value, with an optional second line. */
function Field({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <Box>
      <Typography
        variant="caption"
        component="p"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.25 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * What this count is, as a card rather than two lines of grey prose (owner, 2026-09-18). The window, who opened and
 * closed it and how far it has got are facts a counter and an auditor both read, so each is labelled and the
 * progress is shown, not described.
 */
function CountSummary({
  c,
  tz,
  name,
  verified,
  total,
  contributors,
}: {
  c: CountRow;
  tz: string;
  name(id: string | null): string;
  verified: number;
  total: number;
  contributors: string[];
}) {
  const { t, i18n } = useTranslation();
  const open = c.status === 'open';
  const hours = Math.round((Date.parse(c.window_end) - Date.parse(c.window_start)) / 3_600_000);
  const pct = total ? (verified / total) * 100 : 0;
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1 }} data-testid="count-summary">
      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid size={{ xs: 6, md: 'auto' }}>
          <Field
            label={t('counts.field_status')}
            value={
              <Chip
                size="small"
                color={open ? 'info' : 'default'}
                variant={open ? 'filled' : 'outlined'}
                label={open ? t('counts.status_in_progress') : t('counts.status_closed')}
              />
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 'auto' }}>
          <Field
            label={t('counts.field_window')}
            value={`${dateTime(c.window_start, i18n.language, tz)} – ${dateTime(c.window_end, i18n.language, tz)}`}
            sub={t('counts.window_length', { n: hours })}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 'auto' }}>
          <Field
            label={t('counts.field_opened')}
            value={dateTime(c.created_at, i18n.language, tz)}
            sub={name(c.created_by)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 'auto' }}>
          <Field
            label={t('counts.field_closed')}
            value={
              c.closed_at ? (
                dateTime(c.closed_at, i18n.language, tz)
              ) : (
                <Typography variant="body2" color="text.secondary" component="span">
                  {t('counts.not_closed')}
                </Typography>
              )
            }
            sub={c.closed_at ? name(c.closed_by) : undefined}
          />
        </Grid>
        {contributors.length ? (
          <Grid size={{ xs: 12, md: 'auto' }}>
            <Field label={t('counts.field_counted_by')} value={contributors.join(' · ')} />
          </Grid>
        ) : null}
        {/* the progress belongs with the facts, not next to the filter chips where it read as another filter */}
        <Grid size={{ xs: 12, md: 'grow' }}>
          <Stack spacing={0.5} sx={{ minWidth: 160 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
              >
                {t('counts.col_verified')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('counts.progress', { done: verified, total })}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={total > 0 && verified === total ? 'success' : 'primary'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

export function CountDetailPage() {
  const { t, i18n } = useTranslation();
  const { countId = '' } = useParams();
  const { me } = useSession();
  const count = useCount(countId);
  const status = useCountStatus(count.data?.count.site_id, !!count.data);
  const clear = useClearLine();
  const close = useCloseCount();
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [filter, setFilter] = useState<Bucket | null>(null);
  const lines = count.data?.lines ?? [];
  const names = useProfileNames(
    [
      count.data?.count.created_by,
      count.data?.count.closed_by,
      ...lines.map((l) => l.cleared_by),
    ].filter((x): x is string => !!x),
  );
  useLiveInvalidate(
    `count-${countId}`,
    ['stock_count_lines', 'stock_counts'],
    [['counts', countId]],
  );
  if (count.isLoading) return <Loading />;
  if (count.error || !count.data)
    return (
      <Page title={t('counts.title')}>
        <LoadError error={count.error} />
      </Page>
    );
  const c = count.data.count;
  const open = c.status === 'open';
  const canCount = status.data?.[0]?.can_count ?? false;
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const name = (id: string | null) => (id && names.data?.get(id)) || '—';
  const left = lines.filter((l) => l.bucket === 'not_found' || l.bucket === 'unverified').length;
  const stale = staleCount(isoDateIn(tz, new Date(c.created_at)), isoDateIn(tz));
  // one table, filtered by the bucket chips; expected assets first, then the unexpected ones found here
  const shown = (filter ? lines.filter((l) => l.bucket === filter) : lines)
    .slice()
    .sort(
      (a, b) =>
        BUCKETS.indexOf(a.bucket) - BUCKETS.indexOf(b.bucket) ||
        (a.assets?.asset_code ?? '').localeCompare(b.assets?.asset_code ?? ''),
    );
  const byPerson = new Map<string, number>();
  for (const l of lines)
    if (l.cleared_at) byPerson.set(name(l.cleared_by), (byPerson.get(name(l.cleared_by)) ?? 0) + 1);
  const doClose = () => {
    setConfirming(false);
    close.mutate({ countId, note });
  };

  return (
    <Page
      title={`${t('counts.title')} · ${c.sites?.name ?? ''}`}
      back={
        <Link component={RouterLink} to={`/sites/${c.site_id}?tab=counts`} variant="body2">
          ← {c.sites?.name ?? t('counts.title')}
        </Link>
      }
    >
      <CountSummary
        c={c}
        tz={tz}
        name={name}
        verified={lines.length - left}
        total={lines.length}
        contributors={[...byPerson].map(([n, k]) => `${n} (${k})`)}
      />
      {/* a count open past its day blocks every new count here, and its window keeps widening (owner, 2026-09-18) */}
      {open && stale.stale ? (
        <Alert severity="warning" sx={{ mt: 2 }} data-testid="count-stale">
          <AlertTitle>
            {t('counts.stale_title', {
              date: dateTime(c.created_at, i18n.language, tz),
              days: stale.days,
            })}
          </AlertTitle>
          {t('counts.stale_body')}
        </Alert>
      ) : null}
      {open && status.data && !canCount ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('counts.not_allowed')}
        </Alert>
      ) : null}
      {/* the day's snapshot: one row per asset, and what evidence the count has for each */}
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{ mt: 2 }}
      >
        <Chip
          label={`${t('counts.filter_all')} (${lines.length})`}
          color={filter === null ? 'primary' : 'default'}
          variant={filter === null ? 'filled' : 'outlined'}
          onClick={() => setFilter(null)}
        />
        {BUCKETS.map((b) => {
          const n = lines.filter((l) => l.bucket === b).length;
          return (
            <Chip
              key={b}
              data-testid={`bucket-${b}`}
              label={`${t(`counts.bucket_${b}`)} (${n})`}
              color={filter === b ? BUCKET_COLOR[b] : 'default'}
              variant={filter === b ? 'filled' : 'outlined'}
              onClick={() => setFilter(filter === b ? null : b)}
              disabled={!n}
            />
          );
        })}
      </Stack>

      <Paper variant="outlined" sx={{ mt: 1.5, overflowX: 'auto' }}>
        <Table size="small" data-testid="count-lines">
          <TableHead>
            <TableRow>
              <TableCell>{t('counts.col_asset')}</TableCell>
              <TableCell>{t('counts.status')}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{t('counts.col_verified')}</span>
                  <Tooltip title={t('counts.evidence_help')}>
                    <HelpOutlineIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                  </Tooltip>
                </Stack>
              </TableCell>
              <TableCell>{t('counts.col_when')}</TableCell>
              <TableCell>{t('counts.col_who')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((l) => {
              const v = verifiedBy(l, t);
              const when = l.cleared_at ?? l.found_at;
              const todo = l.bucket === 'not_found' || l.bucket === 'unverified';
              return (
                <TableRow key={l.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {l.assets?.asset_code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {l.assets?.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={BUCKET_COLOR[l.bucket]}
                      variant={l.bucket === 'found' ? 'filled' : 'outlined'}
                      label={t(`counts.bucket_${l.bucket}`)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {v.evidence ? (
                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : null}
                      <Typography
                        variant="body2"
                        color={v.evidence ? 'text.primary' : 'text.secondary'}
                      >
                        {v.label}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {when ? dateTime(when, i18n.language, tz) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {l.cleared_by
                        ? name(l.cleared_by)
                        : // matched by the count itself (a device heard it, or its last position) — nobody ticked it
                          l.found_method && !l.cleared_method
                          ? t('counts.checked_auto')
                          : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {open && canCount && todo ? (
                      <Button
                        size="small"
                        disabled={clear.isPending}
                        onClick={() => clear.mutate({ countId, assetId: l.asset_id })}
                      >
                        {t('counts.confirm_by_eye')}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
            {shown.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {lines.length ? t('counts.no_lines_filter') : t('counts.no_lines')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
      {clear.error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {countError(clear.error, t)}
        </Alert>
      ) : null}
      {open && canCount ? (
        <Box sx={{ mt: 3, maxWidth: 520 }}>
          <TextField
            label={`${t('counts.note')} (${t('common.optional')})`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
          />
          <Button
            variant="contained"
            sx={{ mt: 1 }}
            disabled={close.isPending}
            onClick={() => (left ? setConfirming(true) : doClose())}
          >
            {t('counts.close')}
          </Button>
          {close.error ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {countError(close.error, t)}
            </Alert>
          ) : null}
        </Box>
      ) : !open && c.note ? (
        <Alert severity="info" sx={{ mt: 3 }}>
          {c.note}
        </Alert>
      ) : null}
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs">
        <DialogTitle>{t('counts.close_confirm_title', { n: left })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('counts.close_confirm_body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="warning" onClick={doClose}>
            {t('counts.close_anyway')}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
